/**
 * Onda Dealers — the tavern-casino croupiers' patter (client-only scenery, no wire).
 *
 * A dealer NPC stands behind each blackjack + poker table of the APPLIED capital layout (see portscene.ts) and
 * throws the odd deadpan line: an idle mutter every 20–45 s while a captain is at the felt, plus a reaction to a
 * big win / a natural blackjack / an all-in. Each line is ONE short spoken clip pre-generated by OpenAI TTS
 * (build script tools/dealervoice) and shipped at `public/audio/dealer/<id>.mp3` — never a runtime API call.
 *
 * Two voices keep the two pits distinct: the blackjack croupier is a deep, gravelly growl; the poker dealer is
 * the sly, smoother `poker` voice. `voice` here is BOTH the pit a line belongs to AND the TTS voice it was cut
 * with, so a dealer only ever draws lines tagged for its own game — the audio and the on-screen balloon match.
 */
export type DealerVoice = 'bj' | 'poker';
/** Why a line fires: ambient idle mutter, a greeting when a captain steps up, or a reaction to a table event. */
export type DealerLineKind = 'idle' | 'greet' | 'bigwin' | 'blackjack' | 'allin';

export interface DealerLine {
  /** Stable id → the shipped clip `public/audio/dealer/<id>.mp3`. Never renumber (would orphan an audio file). */
  id: string;
  voice: DealerVoice;
  kind: DealerLineKind;
  /** The balloon text (also the TTS script). Keep it short — one breath, ~1–3 s spoken, deadpan. */
  text: string;
}

/** ~24 short, deadpan pirate croupier lines. Blackjack pit = `bj`; poker pit = `poker`. */
export const DEALER_LINES: readonly DealerLine[] = [
  // ---- Blackjack croupier (deep, gravelly — "One-Eyed Sal") ----
  { id: 'bj_idle_1',  voice: 'bj', kind: 'idle',      text: "House always drinks last, friend." },
  { id: 'bj_idle_2',  voice: 'bj', kind: 'idle',      text: "Cards don't care how ye feel." },
  { id: 'bj_idle_3',  voice: 'bj', kind: 'idle',      text: "Luck's a tide. It goes out." },
  { id: 'bj_idle_4',  voice: 'bj', kind: 'idle',      text: "I've dealt to dead men steadier than you." },
  { id: 'bj_idle_5',  voice: 'bj', kind: 'idle',      text: "Hit, stand, or weep. Your call." },
  { id: 'bj_greet_1', voice: 'bj', kind: 'greet',     text: "Step up. The dealer bites less than the sea." },
  { id: 'bj_greet_2', voice: 'bj', kind: 'greet',     text: "Doubloons on the wood, captain." },
  { id: 'bj_win_1',   voice: 'bj', kind: 'bigwin',    text: "Bah. Enjoy it while it's warm." },
  { id: 'bj_win_2',   voice: 'bj', kind: 'bigwin',    text: "The house remembers a face like yours." },
  { id: 'bj_bj_1',    voice: 'bj', kind: 'blackjack', text: "Twenty-one. The devil's own luck." },
  { id: 'bj_bj_2',    voice: 'bj', kind: 'blackjack', text: "A natural. Even I raise a brow." },
  { id: 'bj_idle_6',  voice: 'bj', kind: 'idle',      text: "Another soul for the felt. Sit." },

  // ---- Poker dealer (sly, smoother — "Lucky Marrow") ----
  { id: 'pk_idle_1',  voice: 'poker', kind: 'idle',   text: "Shuffle up, and pray." },
  { id: 'pk_idle_2',  voice: 'poker', kind: 'idle',   text: "Every hand's a little funeral." },
  { id: 'pk_idle_3',  voice: 'poker', kind: 'idle',   text: "The felt's seen better bluffs than yours." },
  { id: 'pk_idle_4',  voice: 'poker', kind: 'idle',   text: "Chips are just doubloons with ambition." },
  { id: 'pk_idle_5',  voice: 'poker', kind: 'idle',   text: "Fold like laundry, would ye." },
  { id: 'pk_greet_1', voice: 'poker', kind: 'greet',  text: "Buy in, and leave your conscience at the door." },
  { id: 'pk_greet_2', voice: 'poker', kind: 'greet',  text: "A seat's open. So's your purse." },
  { id: 'pk_allin_1', voice: 'poker', kind: 'allin',  text: "All in? Bold, for a breathin' man." },
  { id: 'pk_allin_2', voice: 'poker', kind: 'allin',  text: "Everything on the felt. I love this part." },
  { id: 'pk_win_1',   voice: 'poker', kind: 'bigwin', text: "The pot's yours. This time." },
  { id: 'pk_win_2',   voice: 'poker', kind: 'bigwin', text: "Rake's still mine, mind ye." },
  { id: 'pk_idle_6',  voice: 'poker', kind: 'idle',   text: "Read 'em and weep, or read 'em and leave." },
];

/** All clip ids (drives the lazy audio preload). */
export const DEALER_CLIP_IDS: readonly string[] = DEALER_LINES.map((l) => l.id);

/** Names for each pit (chosen deterministically per table). Blackjack croupiers vs. poker dealers. */
export const BJ_DEALER_NAMES: readonly string[] = ['One-Eyed Sal', 'Gravel-Jaw Job', 'Deadpan Dahlia', 'Old Bones Boyd', 'Ledger Lil', 'Cutpurse Crane'];
export const POKER_DEALER_NAMES: readonly string[] = ['Lucky Marrow', 'Silktongue Vane', 'Ace-Up Ada', 'Whistling Wray', 'Marrow-Bone Fitch', 'Sly Perpetua'];

/** A single planned croupier: which table it stands behind, its pit/voice, and its deterministic name/look seed. */
export interface DealerPlan { stationId: number; game: number; voice: DealerVoice; name: string; tintIdx: number; clsRoll: number; }

/** The (blackjack + poker) casino stations that get a dealer, each with a DETERMINISTIC name/voice/look derived
 *  from (outpostId, stationId, game) — so every client renders the same dealer at the same table. Pure: mirrors
 *  the seeding portscene.buildDealers consumes, and is unit-testable without a WebGL context. `mulb` is the shared
 *  `mulberry32` (passed in to avoid this display-layer module depending on @piratas/sim). */
export function planDealers(
  outpostId: number,
  stations: readonly { id: number; game: number }[],
  mulb: (seed: number) => () => number,
  bjGame: number,
  pokerGame: number,
): DealerPlan[] {
  const out: DealerPlan[] = [];
  for (const st of stations) {
    if (st.game !== bjGame && st.game !== pokerGame) continue;
    const voice: DealerVoice = st.game === pokerGame ? 'poker' : 'bj';
    const rand = mulb(((outpostId * 2654435761) ^ (st.id * 40503) ^ (st.game * 2246822519)) >>> 0);
    const pool = voice === 'poker' ? POKER_DEALER_NAMES : BJ_DEALER_NAMES;
    const name = pool[Math.floor(rand() * pool.length)]!;
    const tintIdx = Math.floor(rand() * 5);
    const clsRoll = Math.floor(rand() * 4);
    out.push({ stationId: st.id, game: st.game, voice, name, tintIdx, clsRoll });
  }
  return out;
}

/** A deterministic line for a dealer of `voice`, filtered by `kind`, chosen by an integer roll (seeded upstream
 *  so every client hears the same dealer say the same thing at the same beat). Falls back to any line of that
 *  voice if the requested kind has none. Returns null only if the voice has no lines at all. */
export function pickDealerLine(voice: DealerVoice, kind: DealerLineKind, roll: number): DealerLine | null {
  let pool = DEALER_LINES.filter((l) => l.voice === voice && l.kind === kind);
  if (!pool.length) pool = DEALER_LINES.filter((l) => l.voice === voice);
  if (!pool.length) return null;
  return pool[Math.abs(roll) % pool.length] ?? null;
}
